import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import parse from 'html-react-parser'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  IoArrowDownCircleOutline,
  IoChatboxEllipses,
  IoCheckmarkCircleOutline,
  IoDocumentAttachOutline,
  IoOpenOutline,
} from 'react-icons/io5'

import type { Alert } from '@/types'

interface FeaturedAlertModalProps {
  alert: Alert | null
  onMarkAsRead: (alertId: number) => void
  isLoading?: boolean
}

function getFileNameFromUrl(url?: string | null): string {
  if (!url) return ''

  try {
    const { pathname } = new URL(url)
    const last = pathname.split('/').pop() || ''

    return decodeURIComponent(last) || 'Visualizar anexo'
  } catch {
    const withoutQuery = url.split('?')[0]
    const last = withoutQuery.split('/').pop() || ''

    return decodeURIComponent(last) || 'Visualizar anexo'
  }
}

function FeaturedAlertModal({
  alert,
  onMarkAsRead,
  isLoading = false,
}: FeaturedAlertModalProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const endSentinelRef = useRef<HTMLDivElement>(null)
  const [hasReachedEnd, setHasReachedEnd] = useState(false)

  const fileName = getFileNameFromUrl(alert?.file)
  const fileExtension = fileName.includes('.')
    ? fileName.split('.').pop()?.toUpperCase()
    : null

  const checkIfReachedBottom = useCallback(() => {
    const el = contentRef.current
    if (!el) return

    const hasNoOverflow = el.scrollHeight <= el.clientHeight + 2
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4

    if (hasNoOverflow || isAtBottom) {
      setHasReachedEnd(true)
    }
  }, [])

  useEffect(() => {
    if (!alert) return

    setHasReachedEnd(false)

    const timer = setTimeout(checkIfReachedBottom, 60)

    const root = contentRef.current
    const sentinel = endSentinelRef.current

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasReachedEnd(true)
        }
      },
      { root, threshold: 0.1 },
    )

    if (sentinel) observer.observe(sentinel)

    return () => {
      clearTimeout(timer)

      observer.disconnect()
    }
  }, [alert, checkIfReachedBottom])

  if (!alert) return null

  const canConfirm = hasReachedEnd && !isLoading

  return (
    <Dialog
      open={Boolean(alert)}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return
        }
      }}
      maxWidth="md"
      fullWidth
      aria-labelledby="featured-alert-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            p: 3,
          },
        },
        backdrop: {
          sx: {
            backdropFilter: 'blur(0.15rem)',
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
          },
        },
      }}>
      <DialogTitle sx={{ p: 0, mb: 2 }} id="featured-alert-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              flexShrink: 0,
            }}>
            <IoChatboxEllipses size={28} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                mb: 0.5,
              }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}>
                {alert.title}
              </Typography>

              {alert.featured && (
                <Chip
                  label="Destacado"
                  size="small"
                  color="primary"
                  sx={{ height: 22, fontWeight: 600 }}
                />
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              Publicado em{' '}
              {new Date(alert.start_date).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ my: 1 }} />

      <DialogContent
        ref={contentRef}
        onScroll={checkIfReachedBottom}
        sx={{
          p: 0,
          py: 2,
          maxHeight: '55vh',
        }}>
        <Box
          sx={{
            color: 'text.primary',
            fontSize: '1rem',
            lineHeight: 1.6,
            '& p': { margin: '0 0 12px 0', '&:last-child': { mb: 0 } },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
          }}>
          {alert.body ? parse(alert.body) : alert.description}
        </Box>

        {(alert.url || alert.file) && (
          <Stack spacing={2} sx={{ mt: 3 }}>
            {alert.url && (
              <Box>
                <Typography
                  component="a"
                  href={alert.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}>
                  Acessar link do comunicado
                  <IoOpenOutline size={16} />
                </Typography>
              </Box>
            )}

            {alert.file && (
              <Box
                component="a"
                href={alert.file}
                target="_blank"
                rel="noopener noreferrer"
                download
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  textDecoration: 'none',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    color: 'primary.main',
                    flexShrink: 0,
                  }}>
                  <IoDocumentAttachOutline size={20} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {fileName}
                    </Typography>
                    {fileExtension && (
                      <Chip
                        label={fileExtension}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Clique para baixar ou visualizar o anexo
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        )}

        <Box ref={endSentinelRef} sx={{ height: 1 }} />
      </DialogContent>

      <Divider sx={{ my: 1 }} />
      <DialogActions sx={{ p: 0, pt: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={!canConfirm}
          onClick={() => onMarkAsRead(alert.id)}
          startIcon={
            !hasReachedEnd ? (
              <IoArrowDownCircleOutline size={20} />
            ) : (
              <IoCheckmarkCircleOutline size={20} />
            )
          }
          sx={{
            py: 1.3,
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-disabled': {
              color: 'text.secondary',
              backgroundColor: 'action.hover',
              border: ({ palette }) => `1px dashed ${palette.divider}`,
            },
          }}>
          {isLoading
            ? 'Confirmando...'
            : !hasReachedEnd
              ? 'Role até o final para confirmar a leitura'
              : 'Estou ciente e li este comunicado'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FeaturedAlertModal
